import { RotateCcw } from "lucide-react";
import React from "react";

import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

interface ResetTableMenuProps {
  /** Reset just the column widths */
  onResetWidths?: () => void;
  /** Reset the order of the columns */
  onResetOrder?: () => void;
  /** Reset column visibility to saved view configuration */
  onResetColumns?: () => void;
  /** Reset table-level filters (search, enum filters, etc.) */
  onResetTableFilters?: () => void;
  /** Reset metric-specific filters – explorer views only */
  onResetMetricFilters?: () => void;
  /** Reset relation filters (people list only) */
  onResetRelationFilters?: () => void;
  /** Unpin all pinned columns */
  onResetPinnedColumns?: () => void;
  /** Reset sort order to default */
  onResetSort?: () => void;
  /** Reset column display aliases */
  onResetAliases?: () => void;
  /** Reset wrapped columns */
  onResetWrappedColumns?: () => void;
  /** Comprehensive reset – required */
  onResetAll: () => void;
}

/**
 * A compact dropdown anchored to the ⟲ icon that exposes granular reset actions.
 * Only shows the menu items for which callbacks are provided.
 */
export function ResetTableMenu({
  onResetWidths,
  onResetOrder,
  onResetColumns,
  onResetTableFilters,
  onResetMetricFilters,
  onResetRelationFilters,
  onResetPinnedColumns,
  onResetSort,
  onResetAliases,
  onResetWrappedColumns,
  onResetAll,
}: ResetTableMenuProps) {
  const [open, setOpen] = React.useState(false);

  const [resetWidths, setResetWidths] = React.useState(false);
  const [resetOrder, setResetOrder] = React.useState(false);
  const [resetColumns, setResetColumns] = React.useState(false);
  const [resetTableFilters, setResetTableFilters] = React.useState(false);
  const [resetMetricFilters, setResetMetricFilters] = React.useState(false);
  const [resetRelationFilters, setResetRelationFilters] = React.useState(false);
  const [resetPinnedColumns, setResetPinnedColumns] = React.useState(false);
  const [resetSort, setResetSort] = React.useState(false);
  const [resetAliases, setResetAliases] = React.useState(false);
  const [resetWrappedColumns, setResetWrappedColumns] = React.useState(false);

  // Clear selections when menu closes
  const clearSelections = React.useCallback(() => {
    setResetWidths(false);
    setResetOrder(false);
    setResetColumns(false);
    setResetTableFilters(false);
    setResetMetricFilters(false);
    setResetRelationFilters(false);
    setResetPinnedColumns(false);
    setResetSort(false);
    setResetAliases(false);
    setResetWrappedColumns(false);
  }, []);

  const applyResets = React.useCallback(() => {
    if (resetWidths) onResetWidths?.();
    if (resetOrder) onResetOrder?.();
    if (resetColumns) onResetColumns?.();
    if (resetTableFilters) onResetTableFilters?.();
    if (resetMetricFilters) onResetMetricFilters?.();
    if (resetRelationFilters) onResetRelationFilters?.();
    if (resetPinnedColumns) onResetPinnedColumns?.();
    if (resetSort) onResetSort?.();
    if (resetAliases) onResetAliases?.();
    if (resetWrappedColumns) onResetWrappedColumns?.();
    setOpen(false);
    clearSelections();
  }, [
    resetWidths,
    resetOrder,
    resetColumns,
    resetTableFilters,
    resetMetricFilters,
    resetRelationFilters,
    resetPinnedColumns,
    resetSort,
    resetAliases,
    resetWrappedColumns,
    onResetWidths,
    onResetOrder,
    onResetColumns,
    onResetTableFilters,
    onResetMetricFilters,
    onResetRelationFilters,
    onResetPinnedColumns,
    onResetSort,
    onResetAliases,
    onResetWrappedColumns,
    clearSelections,
  ]);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        if (!o) clearSelections();
        setOpen(o);
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Reset options</p>
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="w-60">
        {/* Reset all at the top */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onResetAll();
            setOpen(false);
          }}
        >
          Reset all
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        {/* Checkbox items */}
        {onResetWidths && (
          <DropdownMenuCheckboxItem
            checked={resetWidths}
            onCheckedChange={setResetWidths}
            onSelect={(e) => e.preventDefault()}
          >
            Reset column widths
          </DropdownMenuCheckboxItem>
        )}
        {onResetOrder && (
          <DropdownMenuCheckboxItem
            checked={resetOrder}
            onCheckedChange={setResetOrder}
            onSelect={(e) => e.preventDefault()}
          >
            Reset column order
          </DropdownMenuCheckboxItem>
        )}
        {onResetColumns && (
          <DropdownMenuCheckboxItem
            checked={resetColumns}
            onCheckedChange={setResetColumns}
            onSelect={(e) => e.preventDefault()}
          >
            Reset column visibility
          </DropdownMenuCheckboxItem>
        )}
        {onResetTableFilters && (
          <DropdownMenuCheckboxItem
            checked={resetTableFilters}
            onCheckedChange={setResetTableFilters}
            onSelect={(e) => e.preventDefault()}
          >
            Reset table filters
          </DropdownMenuCheckboxItem>
        )}
        {onResetMetricFilters && (
          <DropdownMenuCheckboxItem
            checked={resetMetricFilters}
            onCheckedChange={setResetMetricFilters}
            onSelect={(e) => e.preventDefault()}
          >
            Reset metric filters
          </DropdownMenuCheckboxItem>
        )}
        {onResetRelationFilters && (
          <DropdownMenuCheckboxItem
            checked={resetRelationFilters}
            onCheckedChange={setResetRelationFilters}
            onSelect={(e) => e.preventDefault()}
          >
            Reset relation filters
          </DropdownMenuCheckboxItem>
        )}
        {onResetPinnedColumns && (
          <DropdownMenuCheckboxItem
            checked={resetPinnedColumns}
            onCheckedChange={setResetPinnedColumns}
            onSelect={(e) => e.preventDefault()}
          >
            Reset pinned columns
          </DropdownMenuCheckboxItem>
        )}
        {onResetSort && (
          <DropdownMenuCheckboxItem
            checked={resetSort}
            onCheckedChange={setResetSort}
            onSelect={(e) => e.preventDefault()}
          >
            Reset sort order
          </DropdownMenuCheckboxItem>
        )}
        {onResetAliases && (
          <DropdownMenuCheckboxItem
            checked={resetAliases}
            onCheckedChange={setResetAliases}
            onSelect={(e) => e.preventDefault()}
          >
            Reset column aliases
          </DropdownMenuCheckboxItem>
        )}
        {onResetWrappedColumns && (
          <DropdownMenuCheckboxItem
            checked={resetWrappedColumns}
            onCheckedChange={setResetWrappedColumns}
            onSelect={(e) => e.preventDefault()}
          >
            Reset wrapped columns
          </DropdownMenuCheckboxItem>
        )}

        <div className="border-t mt-2 pt-2 px-2 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={clearSelections}>
            Clear
          </Button>
          <Button
            size="sm"
            onClick={applyResets}
            disabled={
              !(
                resetWidths ||
                resetOrder ||
                resetColumns ||
                resetTableFilters ||
                resetMetricFilters ||
                resetRelationFilters ||
                resetPinnedColumns ||
                resetSort ||
                resetAliases ||
                resetWrappedColumns
              )
            }
          >
            Apply
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ResetTableMenu;
